import { FormEvent, useEffect, useState } from 'react';
import type { AdminInviteCreate, AdminInviteRow, AdminPlanCode, AdminSector } from '@kassa/shared';
import { adminApi, AdminApiError } from '../../api/admin';

// Şirkət dəvəti (KT-84): admin e-poçtla qeydiyyat linki göndərir. Kod ekranda göstərilmir — yalnız «göndərildi, vaxtı».
// «Linki kopyala» yalnız əsas adminə (məktub çatmasa telefonla vermək üçün), hər kopyalama köhnə linki keçərsiz edir.

const SECTORS: [AdminSector, string][] = [
  ['market', 'Market'], ['supermarket', 'Supermarket'], ['wholesale', 'Topdan'], ['agro', 'Aqro'],
  ['restaurant', 'Restoran'], ['clothing', 'Geyim'], ['service', 'Xidmət'],
];
const PLANS: [AdminPlanCode, string][] = [['simple', 'Sadə'], ['standard', 'Standart'], ['full', 'Tam'], ['custom', 'Özün qur']];
const STATUS: Record<AdminInviteRow['status'], string> = { pending: 'Gözləyir', used: 'Qeydiyyat olunub', revoked: 'Ləğv olunub', expired: 'Vaxtı bitib' };
const MAIL: Record<string, string> = { queued: 'növbədə (gündəlik limit və ya göndərilir)', sending: 'göndərilir', sent: 'göndərildi', dead: 'göndərilmədi' };

function errText(err: unknown): string {
  return err instanceof AdminApiError ? err.message : 'Şəbəkə xətası. Yenidən cəhd edin.';
}

function baku(iso: string): string {
  return new Date(Date.parse(iso) + 4 * 3600_000).toISOString().slice(0, 16).replace('T', ' ');
}

const EMPTY: AdminInviteCreate = { name: '', ownerName: '', email: '', sector: 'market', plan: 'simple', trialDays: 30 };

export function Invites({ isOwner }: { isOwner: boolean }) {
  const [rows, setRows] = useState<AdminInviteRow[] | null>(null);
  const [ttl, setTtl] = useState(72);
  const [form, setForm] = useState<AdminInviteCreate | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function load() {
    adminApi
      .invites()
      .then((r) => {
        setRows(r.items);
        setTtl(r.ttlHours);
      })
      .catch((err) => setMsg({ ok: false, text: errText(err) }));
  }
  useEffect(load, []);

  async function run(fn: () => Promise<string>) {
    setBusy(true);
    setMsg(null);
    setLink(null);
    try {
      setMsg({ ok: true, text: await fn() });
      load();
    } catch (err) {
      setMsg({ ok: false, text: errText(err) });
    } finally {
      setBusy(false);
    }
  }

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!form) return;
    const body = { ...form, voen: form.voen || undefined, ownerPhone: form.ownerPhone || undefined };
    await run(async () => {
      const r = await adminApi.inviteCreate(body);
      setForm(null);
      return `Dəvət göndərildi (${form.email}). Link ${baku(r.expiresAt)}-dək keçərlidir.`;
    });
  }

  const set = <K extends keyof AdminInviteCreate>(k: K, v: AdminInviteCreate[K]) => setForm((f) => (f ? { ...f, [k]: v } : f));

  return (
    <section className="card card-wide">
      <div className="toolbar">
        <h2>Dəvətlər</h2>
        <span className="topbar-spacer" />
        {!form && (
          <button type="button" className="btn-primary btn-small" onClick={() => { setForm(EMPTY); setMsg(null); }}>
            + Dəvət göndər
          </button>
        )}
      </div>
      <p className="muted small">Sahibə e-poçtla qeydiyyat linki gedir ({ttl} saat, bir dəfə). Kod ekranda göstərilmir.</p>
      {msg && <p className={msg.ok ? 'ok' : 'bad'} role="status">{msg.text}</p>}
      {link && (
        <div className="lock-panel">
          <p className="small">Yeni link (köhnəsi artıq işləmir). Yalnız telefonla sahibin özünə verin:</p>
          <input className="mono" readOnly value={link} onFocus={(e) => e.currentTarget.select()} aria-label="Qeydiyyat linki" />
        </div>
      )}
      {form && (
        <form className="lock-panel form-stack" onSubmit={create} noValidate>
          <div className="limit-grid">
            <label className="field">
              Şirkətin adı
              <input value={form.name} maxLength={200} onChange={(e) => set('name', e.target.value)} disabled={busy} />
            </label>
            <label className="field">
              VÖEN (könüllü)
              <input inputMode="numeric" maxLength={10} value={form.voen ?? ''} onChange={(e) => set('voen', e.target.value.replace(/\D/g, ''))} disabled={busy} />
            </label>
            <label className="field">
              Sahibin adı
              <input value={form.ownerName} maxLength={120} onChange={(e) => set('ownerName', e.target.value)} disabled={busy} />
            </label>
            <label className="field">
              Sahibin e-poçtu
              <input type="email" value={form.email} maxLength={254} onChange={(e) => set('email', e.target.value)} disabled={busy} />
            </label>
            <label className="field">
              Telefon (könüllü)
              <input value={form.ownerPhone ?? ''} placeholder="+994501234567" onChange={(e) => set('ownerPhone', e.target.value)} disabled={busy} />
            </label>
            <label className="field">
              Fəaliyyət sahəsi
              <select value={form.sector} onChange={(e) => set('sector', e.target.value as AdminSector)} disabled={busy}>
                {SECTORS.map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </label>
            <label className="field">
              Paket
              <select value={form.plan} onChange={(e) => set('plan', e.target.value as AdminPlanCode)} disabled={busy}>
                {PLANS.map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </label>
            <label className="field">
              Sınaq, gün
              <input inputMode="numeric" value={String(form.trialDays ?? 30)} onChange={(e) => set('trialDays', Number(e.target.value.replace(/\D/g, '')) || 0)} disabled={busy} />
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn-primary btn-small" disabled={busy || !form.name.trim() || !form.ownerName.trim() || !form.email.trim()}>
              {busy ? 'Göndərilir…' : 'Dəvət göndər'}
            </button>
            <button type="button" className="btn-small" onClick={() => setForm(null)} disabled={busy}>
              Ləğv et
            </button>
          </div>
        </form>
      )}
      {rows && rows.length === 0 && <p className="muted">Hələ dəvət yoxdur.</p>}
      {rows && rows.length > 0 && (
        <div className="table-scroll">
          <table className="plain">
            <thead>
              <tr>
                <th>Şirkət · sahib</th>
                <th>Status</th>
                <th className="narrow-hide">Məktub</th>
                <th className="narrow-hide">Kim, nə vaxt</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <strong>{r.companyName}</strong>
                    <div className="muted small">
                      {r.ownerName} · {r.email}
                    </div>
                  </td>
                  <td>
                    {STATUS[r.status]}
                    {r.status === 'pending' && <div className="muted small nowrap">{baku(r.expiresAt)}-dək</div>}
                  </td>
                  <td className="narrow-hide small">{r.mailStatus ? (MAIL[r.mailStatus] ?? r.mailStatus) : '—'}</td>
                  <td className="narrow-hide small">
                    {r.createdBy}
                    <div className="muted nowrap">{baku(r.createdAt)}</div>
                  </td>
                  <td className="nowrap">
                    {(r.status === 'pending' || r.status === 'expired') && (
                      <>
                        <button type="button" className="btn-small" disabled={busy} onClick={() => run(async () => `Yenidən göndərildi, ${baku((await adminApi.inviteResend(r.id)).expiresAt)}-dək.`)}>
                          Yenidən göndər
                        </button>{' '}
                        {isOwner && (
                          <button
                            type="button"
                            className="btn-small"
                            disabled={busy}
                            onClick={() =>
                              run(async () => {
                                const x = await adminApi.inviteLink(r.id);
                                setLink(x.link ?? null);
                                return 'Yeni link yaradıldı.';
                              })
                            }
                          >
                            Linki kopyala
                          </button>
                        )}{' '}
                        <button type="button" className="btn-small" disabled={busy} onClick={() => run(async () => (await adminApi.inviteRevoke(r.id), 'Dəvət ləğv olundu.'))}>
                          Ləğv et
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
